import {
  createInspection,
  useListInspections,
  useListProperties,
  useListUnits,
  type InspectionKind,
  type InspectionOut,
} from '@myrentalmate/api-client';
import { Button, Chip, Link as MuiLink, Stack, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { DataTable, type DataColumn } from '../../components/DataTable';
import { FormDialog } from '../../components/FormDialog';
import { required, type Field } from '../../components/FormFields';
import { okData, okItems } from '../../lib/apiData';
import { formatDate, todayIso } from '../../lib/format';
import { useCan } from '../../lib/permissions';
import { KIND } from './inspectionLabels';
import { optionsOf } from './labels';
import { TablePager, useTablePage } from '../../components/TablePager';

const COLUMNS: DataColumn<InspectionOut>[] = [
  {
    label: 'Rental',
    render: (i) => (
      <MuiLink component={Link} to={`/staff/maintenance/inspections/${i.id}`}>
        {i.place}
      </MuiLink>
    ),
  },
  { label: 'Type', render: (i) => KIND[i.kind] },
  { label: 'Date', render: (i) => formatDate(i.inspected_on) },
  { label: 'Inspected by', render: (i) => i.inspector_name },
  {
    label: 'Status',
    render: (i) =>
      i.status === 'draft' ? (
        <Chip size="small" color="warning" label={`Draft, ${i.items_rated} of ${i.items_total}`} />
      ) : (
        <Chip size="small" color="success" label="Completed" />
      ),
  },
  {
    label: 'Problems',
    render: (i) =>
      i.items_flagged > 0 ? <Chip size="small" color="error" label={i.items_flagged} /> : '–',
  },
  {
    label: 'Resident',
    render: (i) =>
      i.resident_ack_at ? 'Acknowledged' : i.status === 'completed' ? 'Waiting' : '–',
  },
];

export function InspectionsPage() {
  const canManage = useCan('maintenance.manage');
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const paging = useTablePage();
  const query = useListInspections(paging.params);
  const inspections = okItems(query.data);
  const properties = useListProperties({ page_size: 200 }).data;
  const units = useListUnits({ page_size: 200 }).data;
  const unitOptions = useMemo(() => {
    const names = new Map(okItems(properties).map((p) => [p.id, p.name]));
    return okItems(units).map((u) => ({
      value: String(u.id),
      label: `${names.get(u.property_id) ?? ''} · ${u.unit_number}`,
    }));
  }, [properties, units]);

  const fields: Field[] = [
    { name: 'unit_id', label: 'Unit', type: 'select', required: true, options: unitOptions },
    {
      name: 'kind',
      label: 'Type',
      type: 'select',
      required: true,
      defaultValue: 'move_in',
      options: optionsOf(KIND),
    },
    { name: 'inspected_on', label: 'Date', type: 'date', required: true },
  ];

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flexGrow: 1 }}>
          <Typography variant="h5">Inspections</Typography>
          <Typography color="text.secondary">
            Walk a unit with a checklist. A move-out shows what each item was like at move-in.
          </Typography>
        </div>
        {canManage && (
          <Button variant="contained" onClick={() => setAdding(true)}>
            Start inspection
          </Button>
        )}
      </Stack>
      {inspections.length === 0 ? (
        <Typography color="text.secondary">No inspections yet.</Typography>
      ) : (
        <>
          <DataTable columns={COLUMNS} rows={inspections} rowKey={(i) => i.id} />
          <TablePager paging={paging} total={okData(query.data)?.total ?? 0} />
        </>
      )}
      {adding && (
        <FormDialog
          title="Start Inspection"
          fields={fields}
          initial={{ unit_id: '', kind: 'move_in', inspected_on: todayIso() }}
          submitLabel="Start"
          onSubmit={async (values) => {
            const made = okData(
              await createInspection({
                unit_id: Number(required(values, 'unit_id')),
                kind: required(values, 'kind') as InspectionKind,
                inspected_on: required(values, 'inspected_on'),
              }),
            );
            if (made) navigate(`/staff/maintenance/inspections/${made.id}`);
          }}
          onClose={() => setAdding(false)}
        />
      )}
    </Stack>
  );
}
